/**
 * EmailScreen — Nachbau der hauseigenen "Briefpost"-Ansicht aus dem bunker.ai
 * Produkt (Use-Case "Kundenanfragen beantworten"). Rendert ausschließlich den
 * Hauptinhalt eines Posteingang-/Antwort-Composers; die App-Shell (Sidebar,
 * Header, Tour-Steuerung) liefert die übergeordnete Demo-Hülle.
 *
 * Drei Regionen wie in einer echten Mail-App:
 *   LINKS   – Posteingang mit Kundenanfragen (ausgewählter Eintrag hervorgehoben)
 *   MITTE   – eingehende Anfrage + KI-Entwurf der Antwort im Editor-Look
 *   RECHTS  – "Ähnliche Vorgänge & Quellen" (Quelldokumente + frühere Tickets)
 *
 * Alle sichtbaren Texte stammen aus dem `demo`-Namespace unter
 * `emailScreen.<scenarioId>.*`. Styling ist unter `.bnk-email` gescoped, die
 * Farben sind die Produkt-Tokens (kein Marketing-Theme). Eine dezente
 * Eintritts-Animation ist an `active` gekoppelt, der Screen wirkt aber auch bei
 * `active === false` fertig.
 */

import {
  Inbox,
  Search,
  PenSquare,
  Sparkles,
  Send,
  Pencil,
  Trash2,
  RefreshCw,
  ShieldCheck,
  FileText,
  CornerUpLeft,
  CheckCircle2,
  Lock,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

const PREFIX = 'emailScreen';

const C = {
  appBg: 'var(--bnkp-appbg, #0D1117)',
  surface: 'var(--bnkp-surface, #141B27)',
  elevated: 'var(--bnkp-elevated, #1A2334)',
  recessed: 'var(--bnkp-recessed, #0A0F1C)',
  sidebar: 'var(--bnkp-sidebar, #070B14)',
  border: 'var(--bnkp-border, #1E2D45)',
  borderStrong: 'var(--bnkp-border2, #30415F)',
  text: 'var(--bnkp-text, #E2E8F0)',
  textSec: 'var(--bnkp-text2, #94A3C2)',
  textTer: 'var(--bnkp-text3, #4A5568)',
  primary: 'var(--bnkp-primary, #2D7EFF)',
  primaryHover: 'var(--bnkp-primary2, #1F6BEF)',
  cyan: 'var(--bnkp-cyan, #00D4FF)',
  green: 'var(--bnkp-green, #34D399)',
  amber: 'var(--bnkp-amber, #FBBF24)',
  red: 'var(--bnkp-red, #FB7185)',
  purple: 'var(--bnkp-purple, #7C3AED)',
} as const;

interface InboxItem {
  id: string;
  company: string;
  sender: string;
  subject: string;
  preview: string;
  time: string;
  unread: boolean;
  priority: string;
  tag: string;
}

interface SourceDoc {
  name: string;
  detail: string;
  relevance: number;
  type: string;
}

interface SimilarTicket {
  ref: string;
  company: string;
  subject: string;
  status: string;
  age: string;
}

// The customer inquiry whose thread is open in the center pane. Generic over
// scenarioId so additional scenarios can be added later without code changes.
const SELECTED_ID = 'k300';

const TAG_TONE: Record<string, { fg: string; bg: string; border: string }> = {
  Reklamation: { fg: C.red, bg: 'rgba(251,113,133,0.12)', border: 'rgba(251,113,133,0.30)' },
  Technik: { fg: C.cyan, bg: 'rgba(0,212,255,0.10)', border: 'rgba(0,212,255,0.26)' },
  Vertrieb: { fg: '#7FB0FF', bg: 'rgba(45,126,255,0.12)', border: 'rgba(45,126,255,0.30)' },
  Vertrag: { fg: '#C4A2FF', bg: 'rgba(124,58,237,0.16)', border: 'rgba(124,58,237,0.34)' },
  Erledigt: { fg: C.green, bg: 'rgba(52,211,153,0.10)', border: 'rgba(52,211,153,0.26)' },
};

const PRIORITY_TONE: Record<string, string> = {
  Hoch: C.red,
  Normal: C.textSec,
  Niedrig: C.textTer,
};

function tagTone(tag: string) {
  return TAG_TONE[tag] ?? { fg: C.textSec, bg: 'rgba(148,163,194,0.10)', border: C.border };
}

export function EmailScreen({
  active = true,
  scenarioId,
  className,
}: {
  active?: boolean;
  scenarioId: string;
  className?: string;
}) {
  const { t } = useTranslation('demo');
  const base = `${PREFIX}.${scenarioId}`;
  const tt = (field: string) => t(`${base}.${field}`);

  const inbox = t(`${base}.inboxItems`, { returnObjects: true }) as InboxItem[];
  const sourceDocs = t(`${base}.sourceDocs`, { returnObjects: true }) as SourceDoc[];
  const tickets = t(`${base}.similarTickets`, { returnObjects: true }) as SimilarTicket[];

  // Defensive: i18n may hand back the raw key string if a fragment is missing.
  const inboxList = Array.isArray(inbox) ? inbox : [];
  const docList = Array.isArray(sourceDocs) ? sourceDocs : [];
  const ticketList = Array.isArray(tickets) ? tickets : [];

  const messageBody = tt('messageBody');
  const replyBody = tt('replyBody');

  return (
    <div
      className={`bnk-email${active ? ' bnk-email--active' : ''}${className ? ` ${className}` : ''}`}
    >
      <style>{css}</style>

      {/* ── App header ──────────────────────────────────────────────── */}
      <header className="bnk-email__head">
        <div className="bnk-email__titlewrap">
          <span className="bnk-email__iconpill" aria-hidden="true">
            <Inbox size={20} strokeWidth={2.2} />
          </span>
          <div>
            <h1 className="bnk-email__title">{tt('headerTitle')}</h1>
            <p className="bnk-email__sub">{tt('headerSubtitle')}</p>
          </div>
        </div>
        <span className="bnk-email__head-badge">
          <span className="bnk-email__dot bnk-email__dot--blue" aria-hidden="true" />
          {tt('headerBadge')}
        </span>
      </header>

      {/* ── Three-pane workspace ────────────────────────────────────── */}
      <div className="bnk-email__work">
        {/* LEFT — inbox rail */}
        <aside className="bnk-email__rail" aria-label={tt('inboxLabel')}>
          <div className="bnk-email__rail-top">
            <div className="bnk-email__rail-head">
              <span className="bnk-email__rail-title">{tt('inboxLabel')}</span>
              <button type="button" className="bnk-email__compose" aria-label={tt('composeButton')}>
                <PenSquare size={15} strokeWidth={2.2} aria-hidden="true" />
              </button>
            </div>
            <label className="bnk-email__search">
              <Search size={15} color={C.textSec} aria-hidden="true" />
              <input type="search" placeholder={tt('inboxSearchPlaceholder')} aria-label={tt('inboxSearchPlaceholder')} />
            </label>
            <div className="bnk-email__filters" role="group" aria-label={tt('inboxLabel')}>
              <button type="button" className="bnk-email__filter bnk-email__filter--on" aria-pressed="true">
                {tt('inboxFilterAll')}
              </button>
              <button type="button" className="bnk-email__filter" aria-pressed="false">
                {tt('inboxFilterOpen')}
              </button>
            </div>
          </div>

          <ul className="bnk-email__list">
            {inboxList.map((m, i) => {
              const selected = m.id === SELECTED_ID;
              const tone = tagTone(m.tag);
              return (
                <li
                  key={m.id ?? i}
                  className={`bnk-email__item bnk-email__rise${selected ? ' bnk-email__item--sel' : ''}`}
                  style={{ animationDelay: `${0.05 + i * 0.05}s` }}
                  aria-current={selected ? 'true' : undefined}
                >
                  <span
                    className={`bnk-email__unread${m.unread ? '' : ' bnk-email__unread--off'}`}
                    aria-hidden="true"
                  />
                  <div className="bnk-email__item-body">
                    <div className="bnk-email__item-row1">
                      <span className={`bnk-email__item-company${m.unread ? ' bnk-email__item-company--unread' : ''}`}>
                        {m.company}
                      </span>
                      <span className="bnk-email__item-time">{m.time}</span>
                    </div>
                    <div className="bnk-email__item-subject">{m.subject}</div>
                    <div className="bnk-email__item-preview">{m.preview}</div>
                    <div className="bnk-email__item-meta">
                      <span
                        className="bnk-email__chip"
                        style={{ color: tone.fg, background: tone.bg, borderColor: tone.border }}
                      >
                        {m.tag}
                      </span>
                      <span
                        className="bnk-email__prio"
                        style={{ color: PRIORITY_TONE[m.priority] ?? C.textSec }}
                      >
                        {m.priority}
                      </span>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </aside>

        {/* CENTER — inquiry + AI draft */}
        <main className="bnk-email__center">
          {/* Incoming inquiry */}
          <section className="bnk-email__msg bnk-email__rise" style={{ animationDelay: '0.10s' }}>
            <div className="bnk-email__msg-head">
              <span className="bnk-email__avatar" aria-hidden="true">{tt('messageAvatarInitials')}</span>
              <div className="bnk-email__msg-from">
                <div className="bnk-email__msg-fromline">
                  <span className="bnk-email__msg-fromname">{tt('messageFrom')}</span>
                  <span className="bnk-email__msg-time">{tt('messageTime')}</span>
                </div>
                <div className="bnk-email__msg-addr">
                  <span className="bnk-email__msg-label">{tt('messageFromLabel')}:</span> {tt('messageFromEmail')}
                  <span className="bnk-email__msg-sep" aria-hidden="true">·</span>
                  <span className="bnk-email__msg-label">{tt('messageToLabel')}:</span> {tt('messageTo')}
                </div>
              </div>
            </div>
            <h2 className="bnk-email__msg-subject">{tt('messageSubject')}</h2>
            <div className="bnk-email__msg-body">
              {messageBody.split('\n').map((line, i) => (
                <p key={i} className={line.trim() === '' ? 'bnk-email__gap' : undefined}>{line || ' '}</p>
              ))}
            </div>
          </section>

          {/* AI-drafted reply (editor card) */}
          <section className="bnk-email__draft bnk-email__rise" style={{ animationDelay: '0.18s' }}>
            <div className="bnk-email__draft-head">
              <span className="bnk-email__reply-icon" aria-hidden="true">
                <CornerUpLeft size={15} strokeWidth={2.2} />
              </span>
              <span className="bnk-email__draft-to">{tt('replyToLabel')}</span>
              <span className="bnk-email__draft-badge">
                <Sparkles size={13} strokeWidth={2.2} aria-hidden="true" />
                {tt('draftBadge')}
              </span>
            </div>

            <div className="bnk-email__draft-meta">
              <span className="bnk-email__meta-pill">
                <Sparkles size={12} strokeWidth={2.2} aria-hidden="true" />
                {tt('draftMeta')}
              </span>
              <span className="bnk-email__meta-pill bnk-email__meta-pill--muted">{tt('draftToneLabel')}</span>
            </div>

            <div className="bnk-email__subjectfield">
              <span className="bnk-email__subjectfield-label">{tt('replySubjectLabel')}</span>
              <span className="bnk-email__subjectfield-value">{tt('replySubject')}</span>
            </div>

            <div className="bnk-email__editor">
              {replyBody.split('\n').map((line, i) => (
                <p key={i} className={line.trim() === '' ? 'bnk-email__gap' : undefined}>{line || ' '}</p>
              ))}
              <span className="bnk-email__caret" aria-hidden="true" />
            </div>

            <div className="bnk-email__toolbar">
              <button type="button" className="bnk-email__btn bnk-email__btn--send">
                <Send size={15} strokeWidth={2.2} aria-hidden="true" />
                {tt('sendButton')}
              </button>
              <button type="button" className="bnk-email__btn">
                <Pencil size={14} strokeWidth={2.2} aria-hidden="true" />
                {tt('editButton')}
              </button>
              <button type="button" className="bnk-email__btn">
                <RefreshCw size={14} strokeWidth={2.2} aria-hidden="true" />
                {tt('regenerateButton')}
              </button>
              <button type="button" className="bnk-email__btn bnk-email__btn--ghost">
                <Trash2 size={14} strokeWidth={2.2} aria-hidden="true" />
                {tt('discardButton')}
              </button>
              <span className="bnk-email__toolbar-hint">{tt('toolbarHint')}</span>
            </div>
          </section>
        </main>

        {/* RIGHT — sources & similar tickets */}
        <aside className="bnk-email__side bnk-email__rise" style={{ animationDelay: '0.22s' }} aria-label={tt('sourcesTitle')}>
          <div className="bnk-email__side-head">
            <div>
              <h2 className="bnk-email__side-title">{tt('sourcesTitle')}</h2>
              <p className="bnk-email__side-sub">{tt('sourcesSubtitle')}</p>
            </div>
            <span className="bnk-email__verified">
              <ShieldCheck size={13} strokeWidth={2.2} aria-hidden="true" />
              {tt('verifiedChip')}
            </span>
          </div>

          <div className="bnk-email__side-section">
            <div className="bnk-email__side-heading">
              <FileText size={13} strokeWidth={2.2} aria-hidden="true" />
              {tt('sourcesDocsHeading')}
            </div>
            <ul className="bnk-email__docs">
              {docList.map((d, i) => (
                <li key={d.name ?? i} className="bnk-email__doc">
                  <div className="bnk-email__doc-top">
                    <span className="bnk-email__doc-icon" aria-hidden="true">
                      <FileText size={15} strokeWidth={2.1} />
                    </span>
                    <div className="bnk-email__doc-text">
                      <span className="bnk-email__doc-name" title={d.name}>{d.name}</span>
                      <span className="bnk-email__doc-detail">{d.detail}</span>
                    </div>
                    <span className="bnk-email__doc-type">{d.type}</span>
                  </div>
                  <div className="bnk-email__rel">
                    <div className="bnk-email__rel-track">
                      <div
                        className="bnk-email__rel-fill"
                        style={{ width: `${d.relevance}%` }}
                      />
                    </div>
                    <span className="bnk-email__rel-val">
                      {tt('relevanceLabel')} {d.relevance} %
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          <div className="bnk-email__side-section">
            <div className="bnk-email__side-heading">
              <CornerUpLeft size={13} strokeWidth={2.2} aria-hidden="true" />
              {tt('ticketsHeading')}
            </div>
            <ul className="bnk-email__tickets">
              {ticketList.map((tk, i) => (
                <li key={tk.ref ?? i} className="bnk-email__ticket">
                  <div className="bnk-email__ticket-row1">
                    <span className="bnk-email__ticket-ref">{tk.ref}</span>
                    <span className="bnk-email__ticket-status">
                      <CheckCircle2 size={12} strokeWidth={2.4} aria-hidden="true" />
                      {tk.status}
                    </span>
                  </div>
                  <div className="bnk-email__ticket-subject">{tk.subject}</div>
                  <div className="bnk-email__ticket-meta">
                    <span>{tk.company}</span>
                    <span className="bnk-email__msg-sep" aria-hidden="true">·</span>
                    <span>{tk.age}</span>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          <p className="bnk-email__side-foot">
            <Lock size={12} strokeWidth={2.2} aria-hidden="true" />
            {tt('panelFooter')}
          </p>
        </aside>
      </div>
    </div>
  );
}

export default EmailScreen;

const css = `
.bnk-email {
  --eml-bg: ${C.appBg}; --eml-surface: ${C.surface}; --eml-elev: ${C.elevated};
  --eml-recessed: ${C.recessed}; --eml-sidebar: ${C.sidebar};
  --eml-border: ${C.border}; --eml-border2: ${C.borderStrong};
  --eml-text: ${C.text}; --eml-sec: ${C.textSec}; --eml-ter: ${C.textTer};
  --eml-primary: ${C.primary}; --eml-primary2: ${C.primaryHover}; --eml-cyan: ${C.cyan};
  --eml-green: ${C.green}; --eml-amber: ${C.amber}; --eml-red: ${C.red}; --eml-purple: ${C.purple};
  font-family: Inter, system-ui, sans-serif;
  color: var(--eml-text);
  width: 100%; height: 100%;
  display: flex; flex-direction: column; min-height: 0;
  box-sizing: border-box;
}
.bnk-email *, .bnk-email *::before, .bnk-email *::after { box-sizing: border-box; }

/* entrance animation — gated on .bnk-email--active; finished look otherwise */
@keyframes bnk-email-rise {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: none; }
}
.bnk-email__rise { opacity: 1; }
.bnk-email--active .bnk-email__rise {
  opacity: 0; animation: bnk-email-rise .5s cubic-bezier(.22,.61,.36,1) forwards;
}

/* ── Header ────────────────────────────────────────────────────── */
.bnk-email__head {
  display: flex; align-items: flex-start; justify-content: space-between;
  gap: 16px; flex-wrap: wrap; margin-bottom: 16px; flex: none;
}
.bnk-email__titlewrap { display: flex; align-items: center; gap: 13px; }
.bnk-email__iconpill {
  display: grid; place-items: center; width: 40px; height: 40px; flex: none;
  border-radius: 12px; color: var(--eml-primary);
  background: rgba(45,126,255,0.10); border: 1px solid rgba(45,126,255,0.22);
}
.bnk-email__title { margin: 0; font-size: 21px; font-weight: 700; letter-spacing: -0.01em; line-height: 1.15; }
.bnk-email__sub { margin: 3px 0 0; font-size: 13px; color: var(--eml-sec); max-width: 640px; }
.bnk-email__head-badge {
  display: inline-flex; align-items: center; gap: 8px; flex: none;
  padding: 7px 13px; border-radius: 999px; font-size: 12.5px; font-weight: 600;
  color: var(--eml-sec); background: rgba(255,255,255,0.04);
  border: 1px solid var(--eml-border); backdrop-filter: blur(12px);
}
.bnk-email__dot { width: 7px; height: 7px; border-radius: 50%; }
.bnk-email__dot--blue {
  background: var(--eml-primary);
  box-shadow: 0 0 0 0 rgba(45,126,255,0.6);
  animation: bnk-email-pulse 2.4s ease-out infinite;
}
@keyframes bnk-email-pulse {
  0% { box-shadow: 0 0 0 0 rgba(45,126,255,0.55); }
  70% { box-shadow: 0 0 0 6px rgba(45,126,255,0); }
  100% { box-shadow: 0 0 0 0 rgba(45,126,255,0); }
}

/* ── Workspace grid ───────────────────────────────────────────── */
.bnk-email__work {
  display: grid; grid-template-columns: 260px minmax(0, 1fr) 320px;
  gap: 14px; flex: 1 1 auto; min-height: 0;
}

/* shared panel surface */
.bnk-email__rail, .bnk-email__side {
  background: var(--eml-surface); border: 1px solid var(--eml-border);
  border-radius: 14px; display: flex; flex-direction: column; min-height: 0;
  box-shadow: 0 1px 2px rgba(0,0,0,0.3);
}

/* ── LEFT rail ────────────────────────────────────────────────── */
.bnk-email__rail-top {
  padding: 14px 12px 12px; border-bottom: 1px solid var(--eml-border);
  background: var(--eml-sidebar); border-radius: 14px 14px 0 0; flex: none;
}
.bnk-email__rail-head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 11px; }
.bnk-email__rail-title { font-size: 13px; font-weight: 700; letter-spacing: 0.01em; }
.bnk-email__compose {
  display: grid; place-items: center; width: 30px; height: 30px; cursor: pointer;
  border-radius: 9px; color: #fff; border: none;
  background: linear-gradient(180deg, var(--eml-primary) 0%, var(--eml-primary2) 100%);
  box-shadow: 0 3px 10px rgba(45,126,255,0.34); transition: filter .15s;
}
.bnk-email__compose:hover { filter: brightness(1.08); }
.bnk-email__search {
  display: flex; align-items: center; gap: 8px; height: 34px; padding: 0 11px;
  background: var(--eml-recessed); border: 1px solid var(--eml-border);
  border-radius: 9px; margin-bottom: 10px;
}
.bnk-email__search input {
  flex: 1; min-width: 0; background: transparent; border: none; outline: none;
  color: var(--eml-text); font: inherit; font-size: 12.5px;
}
.bnk-email__search input::placeholder { color: var(--eml-ter); }
.bnk-email__search:focus-within { border-color: var(--eml-border2); }
.bnk-email__filters { display: flex; gap: 6px; }
.bnk-email__filter {
  flex: 1; height: 28px; cursor: pointer; font: inherit; font-size: 12px; font-weight: 600;
  border-radius: 8px; color: var(--eml-sec);
  background: rgba(255,255,255,0.03); border: 1px solid var(--eml-border);
  transition: background .15s, color .15s, border-color .15s;
}
.bnk-email__filter:hover { color: var(--eml-text); border-color: var(--eml-border2); }
.bnk-email__filter--on {
  color: var(--eml-text); background: rgba(45,126,255,0.14); border-color: rgba(45,126,255,0.36);
}

.bnk-email__list {
  list-style: none; margin: 0; padding: 6px; overflow-y: auto; flex: 1 1 auto; min-height: 0;
  scrollbar-width: none;
}
.bnk-email__list::-webkit-scrollbar { width: 0; height: 0; }
.bnk-email__item {
  display: flex; gap: 9px; padding: 11px 10px; border-radius: 11px; cursor: pointer;
  border: 1px solid transparent; transition: background .15s, border-color .15s;
}
.bnk-email__item + .bnk-email__item { margin-top: 3px; }
.bnk-email__item:hover { background: rgba(255,255,255,0.035); }
.bnk-email__item--sel {
  background: var(--eml-elev); border-color: rgba(45,126,255,0.45);
  box-shadow: inset 3px 0 0 0 var(--eml-primary), 0 0 18px rgba(45,126,255,0.12);
}
.bnk-email__unread {
  width: 8px; height: 8px; flex: none; margin-top: 5px; border-radius: 50%;
  background: var(--eml-primary); box-shadow: 0 0 8px rgba(45,126,255,0.7);
}
.bnk-email__unread--off { background: transparent; box-shadow: none; border: 1px solid var(--eml-border2); }
.bnk-email__item-body { min-width: 0; flex: 1; }
.bnk-email__item-row1 { display: flex; align-items: baseline; justify-content: space-between; gap: 8px; }
.bnk-email__item-company {
  font-size: 12.5px; color: var(--eml-sec); font-weight: 600;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.bnk-email__item-company--unread { color: var(--eml-text); }
.bnk-email__item-time { font-size: 11px; color: var(--eml-ter); flex: none; }
.bnk-email__item-subject {
  font-size: 13px; font-weight: 600; color: var(--eml-text); margin: 3px 0 2px;
  line-height: 1.3; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.bnk-email__item-preview {
  font-size: 11.5px; color: var(--eml-sec); line-height: 1.4;
  display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;
}
.bnk-email__item-meta { display: flex; align-items: center; gap: 8px; margin-top: 8px; }
.bnk-email__chip {
  display: inline-flex; align-items: center; font-size: 10.5px; font-weight: 700;
  letter-spacing: 0.02em; padding: 2px 8px; border-radius: 999px; border: 1px solid transparent;
}
.bnk-email__prio { font-size: 11px; font-weight: 600; }

/* ── CENTER ───────────────────────────────────────────────────── */
.bnk-email__center {
  display: flex; flex-direction: column; gap: 14px; min-height: 0; overflow-y: auto;
  padding-right: 2px; scrollbar-width: none;
}
.bnk-email__center::-webkit-scrollbar { width: 0; height: 0; }

.bnk-email__msg {
  background: var(--eml-surface); border: 1px solid var(--eml-border);
  border-radius: 14px; padding: 18px 20px; box-shadow: 0 1px 2px rgba(0,0,0,0.3);
}
.bnk-email__msg-head { display: flex; align-items: center; gap: 12px; margin-bottom: 14px; }
.bnk-email__avatar {
  display: grid; place-items: center; width: 40px; height: 40px; flex: none; border-radius: 50%;
  background: linear-gradient(135deg, var(--eml-purple), #A855F7); color: #fff;
  font-size: 14px; font-weight: 700; letter-spacing: 0.02em;
}
.bnk-email__msg-from { min-width: 0; flex: 1; }
.bnk-email__msg-fromline { display: flex; align-items: baseline; gap: 10px; flex-wrap: wrap; }
.bnk-email__msg-fromname { font-size: 14.5px; font-weight: 700; color: var(--eml-text); }
.bnk-email__msg-time { font-size: 12px; color: var(--eml-ter); }
.bnk-email__msg-addr {
  font-size: 12px; color: var(--eml-sec); margin-top: 3px;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.bnk-email__msg-label { color: var(--eml-ter); }
.bnk-email__msg-sep { color: var(--eml-ter); margin: 0 7px; }
.bnk-email__msg-subject {
  margin: 0 0 12px; font-size: 16px; font-weight: 700; letter-spacing: -0.01em;
  padding-bottom: 12px; border-bottom: 1px solid var(--eml-border);
}
.bnk-email__msg-body { font-size: 13.5px; line-height: 1.62; color: var(--eml-text); }
.bnk-email__msg-body p { margin: 0; }
.bnk-email__msg-body p + p { margin-top: 4px; }
.bnk-email__gap { height: 8px; }

/* ── Draft (AI reply) ─────────────────────────────────────────── */
.bnk-email__draft {
  background: var(--eml-surface);
  border: 1px solid rgba(45,126,255,0.34);
  border-radius: 14px; padding: 16px 20px 18px;
  box-shadow: 0 0 0 1px rgba(45,126,255,0.10), 0 10px 30px rgba(45,126,255,0.10);
  position: relative; overflow: hidden;
}
.bnk-email__draft::before {
  content: ''; position: absolute; inset: 0 0 auto 0; height: 2px;
  background: linear-gradient(90deg, var(--eml-primary), var(--eml-cyan));
}
.bnk-email__draft-head { display: flex; align-items: center; gap: 10px; margin-bottom: 12px; flex-wrap: wrap; }
.bnk-email__reply-icon {
  display: grid; place-items: center; width: 28px; height: 28px; flex: none;
  border-radius: 8px; color: var(--eml-primary);
  background: rgba(45,126,255,0.12); border: 1px solid rgba(45,126,255,0.26);
}
.bnk-email__draft-to { font-size: 13.5px; font-weight: 700; color: var(--eml-text); margin-right: auto; }
.bnk-email__draft-badge {
  display: inline-flex; align-items: center; gap: 6px; flex: none;
  padding: 5px 11px; border-radius: 999px; font-size: 11.5px; font-weight: 700;
  color: #fff; background: linear-gradient(135deg, var(--eml-primary), var(--eml-purple));
  box-shadow: 0 3px 12px rgba(45,126,255,0.34);
}
.bnk-email__draft-meta { display: flex; align-items: center; gap: 8px; margin-bottom: 12px; flex-wrap: wrap; }
.bnk-email__meta-pill {
  display: inline-flex; align-items: center; gap: 6px;
  font-size: 11.5px; font-weight: 600; color: var(--eml-cyan);
  padding: 4px 10px; border-radius: 999px;
  background: rgba(0,212,255,0.08); border: 1px solid rgba(0,212,255,0.22);
}
.bnk-email__meta-pill--muted {
  color: var(--eml-sec); background: rgba(255,255,255,0.03); border-color: var(--eml-border);
}
.bnk-email__subjectfield {
  display: flex; align-items: baseline; gap: 10px; padding: 9px 12px; margin-bottom: 12px;
  background: var(--eml-recessed); border: 1px solid var(--eml-border); border-radius: 9px;
}
.bnk-email__subjectfield-label { font-size: 11.5px; color: var(--eml-ter); flex: none; font-weight: 600; }
.bnk-email__subjectfield-value {
  font-size: 13px; font-weight: 600; color: var(--eml-text);
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.bnk-email__editor {
  font-size: 13.5px; line-height: 1.62; color: var(--eml-text);
  padding: 14px 15px; border-radius: 11px; min-height: 120px;
  background: var(--eml-recessed); border: 1px solid var(--eml-border);
}
.bnk-email__editor p { margin: 0; }
.bnk-email__editor p + p { margin-top: 4px; }
.bnk-email__caret {
  display: inline-block; width: 2px; height: 15px; vertical-align: text-bottom;
  margin-left: 1px; background: var(--eml-primary); animation: bnk-email-blink 1.1s step-end infinite;
}
@keyframes bnk-email-blink { 0%, 100% { opacity: 1; } 50% { opacity: 0; } }

.bnk-email__toolbar {
  display: flex; align-items: center; gap: 9px; margin-top: 14px; flex-wrap: wrap;
}
.bnk-email__btn {
  display: inline-flex; align-items: center; gap: 7px; cursor: pointer; font: inherit;
  font-size: 13px; font-weight: 600; padding: 8px 14px; border-radius: 9px;
  color: var(--eml-text); background: var(--eml-elev); border: 1px solid var(--eml-border2);
  transition: background .15s, border-color .15s, color .15s;
}
.bnk-email__btn:hover { background: rgba(255,255,255,0.06); border-color: var(--eml-border2); }
.bnk-email__btn--send {
  color: #fff; border: none;
  background: linear-gradient(180deg, var(--eml-primary) 0%, var(--eml-primary2) 100%);
  box-shadow: 0 4px 14px rgba(45,126,255,0.34);
}
.bnk-email__btn--send:hover { filter: brightness(1.08); background: linear-gradient(180deg, var(--eml-primary) 0%, var(--eml-primary2) 100%); }
.bnk-email__btn--ghost { color: var(--eml-sec); background: transparent; border-color: var(--eml-border); }
.bnk-email__btn--ghost:hover { color: var(--eml-red); border-color: rgba(251,113,133,0.4); background: rgba(251,113,133,0.08); }
.bnk-email__toolbar-hint {
  display: inline-flex; align-items: center; font-size: 11.5px; color: var(--eml-ter);
  margin-left: auto;
}

/* ── RIGHT side panel ─────────────────────────────────────────── */
.bnk-email__side { overflow: hidden; }
.bnk-email__side-head {
  display: flex; align-items: flex-start; justify-content: space-between; gap: 10px;
  padding: 15px 16px 13px; border-bottom: 1px solid var(--eml-border);
  background: var(--eml-sidebar); border-radius: 14px 14px 0 0; flex: none; flex-wrap: wrap;
}
.bnk-email__side-title { margin: 0; font-size: 13.5px; font-weight: 700; }
.bnk-email__side-sub { margin: 3px 0 0; font-size: 11.5px; color: var(--eml-sec); }
.bnk-email__verified {
  display: inline-flex; align-items: center; gap: 6px; flex: none;
  padding: 5px 10px; border-radius: 999px; font-size: 11px; font-weight: 700;
  color: var(--eml-green); background: rgba(52,211,153,0.10); border: 1px solid rgba(52,211,153,0.26);
}

.bnk-email__side-section { padding: 14px 16px; overflow-y: auto; }
.bnk-email__side-section:first-of-type { border-bottom: 1px solid var(--eml-border); }
.bnk-email__side-heading {
  display: flex; align-items: center; gap: 7px; margin-bottom: 11px;
  font-size: 11px; font-weight: 700; letter-spacing: 0.05em; text-transform: uppercase;
  color: var(--eml-ter);
}
.bnk-email__side-heading svg { color: var(--eml-sec); }

.bnk-email__docs, .bnk-email__tickets { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 10px; }
.bnk-email__doc {
  background: var(--eml-recessed); border: 1px solid var(--eml-border); border-radius: 11px; padding: 11px 12px;
  transition: border-color .15s;
}
.bnk-email__doc:hover { border-color: var(--eml-border2); }
.bnk-email__doc-top { display: flex; align-items: center; gap: 9px; }
.bnk-email__doc-icon {
  display: grid; place-items: center; width: 30px; height: 30px; flex: none; border-radius: 8px;
  color: var(--eml-primary); background: rgba(45,126,255,0.10); border: 1px solid rgba(45,126,255,0.20);
}
.bnk-email__doc-text { min-width: 0; flex: 1; display: flex; flex-direction: column; }
.bnk-email__doc-name {
  font-size: 12.5px; font-weight: 600; color: var(--eml-text);
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.bnk-email__doc-detail {
  font-size: 11px; color: var(--eml-sec); margin-top: 1px;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.bnk-email__doc-type {
  flex: none; font-size: 9.5px; font-weight: 700; letter-spacing: 0.05em; color: var(--eml-ter);
  padding: 2px 6px; border-radius: 5px; background: rgba(255,255,255,0.04); border: 1px solid var(--eml-border);
}
.bnk-email__rel { display: flex; align-items: center; gap: 9px; margin-top: 9px; }
.bnk-email__rel-track {
  flex: 1; height: 5px; border-radius: 999px; overflow: hidden; background: var(--eml-elev);
}
.bnk-email__rel-fill {
  height: 100%; border-radius: 999px;
  background: linear-gradient(90deg, var(--eml-primary), var(--eml-cyan));
  transition: width .8s cubic-bezier(.22,.61,.36,1);
}
.bnk-email__rel-val { flex: none; font-size: 11px; font-weight: 600; color: var(--eml-sec); font-variant-numeric: tabular-nums; }

.bnk-email__ticket {
  background: var(--eml-recessed); border: 1px solid var(--eml-border); border-radius: 11px; padding: 11px 12px;
  transition: border-color .15s;
}
.bnk-email__ticket:hover { border-color: var(--eml-border2); }
.bnk-email__ticket-row1 { display: flex; align-items: center; justify-content: space-between; gap: 8px; }
.bnk-email__ticket-ref { font-size: 11.5px; font-weight: 700; color: var(--eml-sec); font-variant-numeric: tabular-nums; letter-spacing: 0.02em; }
.bnk-email__ticket-status {
  display: inline-flex; align-items: center; gap: 5px; flex: none;
  font-size: 10.5px; font-weight: 700; color: var(--eml-green);
  padding: 2px 8px; border-radius: 999px; background: rgba(52,211,153,0.10); border: 1px solid rgba(52,211,153,0.24);
}
.bnk-email__ticket-subject {
  font-size: 12.5px; font-weight: 600; color: var(--eml-text); margin: 6px 0 4px; line-height: 1.35;
}
.bnk-email__ticket-meta { display: flex; align-items: center; font-size: 11px; color: var(--eml-sec); }

.bnk-email__side-foot {
  display: flex; align-items: center; gap: 7px; margin: 0;
  padding: 11px 16px 14px; font-size: 11px; color: var(--eml-ter);
  border-top: 1px solid var(--eml-border); flex: none;
}

/* ── Responsive collapse ──────────────────────────────────────── */
@media (max-width: 1080px) {
  .bnk-email__work { grid-template-columns: 232px minmax(0, 1fr); }
  .bnk-email__side { display: none; }
}
@media (max-width: 760px) {
  .bnk-email__work { grid-template-columns: 1fr; }
  .bnk-email__rail { display: none; }
}
@media (prefers-reduced-motion: reduce) {
  .bnk-email--active .bnk-email__rise { animation: none; opacity: 1; }
  .bnk-email__dot--blue { animation: none; }
  .bnk-email__caret { animation: none; }
  .bnk-email__rel-fill { transition: none; }
}
`;
